import { useCallback, useEffect, useMemo, useState } from 'react'
import { Clock3, PenLine, RefreshCw, Search, ShieldAlert, Users } from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { apiGet, usePermissions } from '../admin/primitives.js'
import { Combobox, SegmentedControl, type ComboItem } from '../admin/pickers.js'
import { TextInput } from '../admin/form.js'
import { useMenuPersonne } from './menu-personne.js'
import './rh.css'
import './presence.css'

/**
 * BORNE NATIVE — PRÉSENCE TEMPS RÉEL (Gestion).
 *
 * Un seul read model derrière cet écran : `presenceBoard`. La fiche soirée, la
 * borne iPad et la future Main Courante lisent EXACTEMENT la même chose. Rien
 * n'est recalculé ici — pas un état, pas une minute.
 *
 * Deux honnêtetés d'affichage :
 *  · « Actualisé il y a X s », jamais « LIVE » : c'est un sondage, pas un flux.
 *  · un shift au planning ne vaut PAS une présence. Une personne n'est comptée
 *    « signée » que si son entrée porte une preuve manuscrite.
 */

type Etat = 'OFF_SHIFT' | 'UPCOMING_SHIFT' | 'EXPECTED_LATE' | 'WORKING' | 'WORKING_UNPLANNED' | 'ON_BREAK' | 'FINISHED'

interface Shift {
  shift_id: string | null; planned: boolean
  starts_at: string; ends_at: string
  break_minutes: number; break_paid: boolean
  position_name: string | null; location_name: string | null
  team_name: string | null; event_name: string | null
}
interface Resume {
  planned_minutes: number | null; presence_minutes: number; break_minutes: number; break_count: number
  paid_break_minutes: number; unpaid_break_minutes: number; worked_minutes: number; paid_minutes: number
}
interface Personne {
  person_id: string; display_name: string; position_name: string | null
  team_id: string | null; team_name: string | null
  state: Etat
  shift: Shift | null
  clock_in_at: string | null; clock_out_at: string | null
  current_break: { starts_at: string; minutes: number } | null
  breaks: Array<{ starts_at: string; ends_at: string | null; minutes: number }>
  entry_signed: boolean
  summary: Resume
  last_transition_at: string | null
}
interface Tableau {
  at: string
  counts: Record<Etat, number> & { total: number; present: number }
  people: Personne[]
}

const PARIS = 'Europe/Paris'
const heure = (iso: string | null): string =>
  iso ? new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: PARIS }).format(new Date(iso)) : '—'

const duree = (min: number): string => {
  const m = Math.max(0, Math.round(min))
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`
}

/** Le libellé d'un état est un fait métier : il ne s'invente pas dans le composant. */
const ETATS: Record<Etat, { label: string; ton: string }> = {
  WORKING: { label: 'En poste', ton: 'emerald' },
  WORKING_UNPLANNED: { label: 'Hors planning', ton: 'violet' },
  ON_BREAK: { label: 'En pause', ton: 'cyan' },
  EXPECTED_LATE: { label: 'Pas encore arrivé', ton: 'amber' },
  UPCOMING_SHIFT: { label: 'Attendu', ton: 'blue' },
  FINISHED: { label: 'Terminé', ton: 'slate' },
  OFF_SHIFT: { label: 'Hors service', ton: 'slate' },
}

function Pastille({ etat }: { etat: Etat }) {
  const e = ETATS[etat]
  // L'état n'est PAS porté par la seule couleur : le mot est toujours écrit.
  return <span className={`pr-etat pr-etat--${e.ton}`}><i aria-hidden="true" />{e.label}</span>
}

function Compteur({ label, valeur, ton, actif, onClick }: {
  label: string; valeur: number; ton: string; actif: boolean; onClick: () => void
}) {
  return (
    <button type="button" className={`pr-kpi pr-kpi--${ton}${actif ? ' is-actif' : ''}`}
      aria-pressed={actif} onClick={onClick}>
      <span className="pr-kpi__n">{valeur}</span>
      <span className="pr-kpi__l">{label}</span>
    </button>
  )
}

export function HrPresencePage() {
  const perms = usePermissions()
  const menu = useMenuPersonne()
  const peut = (k: string): boolean => perms.permissions.includes(k)
  const [data, setData] = useState<Tableau | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [charge, setCharge] = useState(false)
  const [vuA, setVuA] = useState<number>(Date.now())
  const [filtreEtat, setFiltreEtat] = useState<Etat | 'TOUS'>('TOUS')
  const [equipe, setEquipe] = useState<string | null>(null)
  const [poste, setPoste] = useState<string | null>(null)
  const [lieu, setLieu] = useState<string | null>(null)
  const [signature, setSignature] = useState<'TOUS' | 'SIGNEE' | 'NON'>('TOUS')
  const [q, setQ] = useState('')

  const charger = useCallback(async () => {
    setCharge(true)
    try {
      const r = await apiGet<Tableau>('/api/v1/hr/presence')
      setData(r); setErreur(null); setVuA(Date.now())
    } catch {
      // Un écran de présence qui ment est pire qu'un écran vide.
      setErreur('Impossible de lire la présence pour le moment.')
    } finally { setCharge(false) }
  }, [])

  useEffect(() => { void charger() }, [charger])
  useEffect(() => {
    // Sondage régulier — pas de WebSocket pour ce seul écran.
    const t = setInterval(() => { void charger() }, 20_000)
    return () => clearInterval(t)
  }, [charger])

  const [tick, setTick] = useState(0)
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 1000); return () => clearInterval(t) }, [])
  const fraicheur = useMemo(() => {
    void tick
    const s = Math.round((Date.now() - vuA) / 1000)
    return s < 5 ? 'à l’instant' : s < 60 ? `il y a ${s} s` : `il y a ${Math.round(s / 60)} min`
  }, [vuA, tick])

  const equipes = useMemo<ComboItem[]>(() => {
    const m = new Map<string, string>()
    for (const p of data?.people ?? []) if (p.team_id && p.team_name) m.set(p.team_id, p.team_name)
    return [...m].map(([value, label]) => ({ value, label }))
  }, [data])
  const postes = useMemo<ComboItem[]>(() => {
    const s = new Set<string>()
    for (const p of data?.people ?? []) if (p.position_name) s.add(p.position_name)
    return [...s].sort().map((x) => ({ value: x, label: x }))
  }, [data])
  const lieux = useMemo<ComboItem[]>(() => {
    const s = new Set<string>()
    for (const p of data?.people ?? []) if (p.shift?.location_name) s.add(p.shift.location_name)
    return [...s].sort().map((x) => ({ value: x, label: x }))
  }, [data])

  const gens = useMemo(() => {
    const t = q.trim().toLowerCase()
    return (data?.people ?? []).filter((p) => {
      if (filtreEtat !== 'TOUS' && p.state !== filtreEtat) return false
      if (equipe && p.team_id !== equipe) return false
      if (poste && p.position_name !== poste) return false
      if (lieu && p.shift?.location_name !== lieu) return false
      if (signature === 'SIGNEE' && !p.entry_signed) return false
      if (signature === 'NON' && p.entry_signed) return false
      if (t && !p.display_name.toLowerCase().includes(t)) return false
      return true
    })
  }, [data, filtreEtat, equipe, poste, lieu, signature, q])

  if (!peut('time.read.team')) {
    return <div className="gd-panel adm-note">Vous n’avez pas le droit de consulter la présence de l’équipe.</div>
  }

  const c = data?.counts
  return (
    <div className="lc-page">
      <SmartBack />
      <Trail />
      <header className="hub-hero">
        <p className="hub-hero__kicker"><Users size={13} aria-hidden="true" /> Temps</p>
        <h1>Présence</h1>
        <p className="hub-hero__sub">
          Qui est réellement en poste, maintenant. La source est le pointage, jamais le planning seul.
        </p>
      </header>

      <div className="pr-barre">
        <span className="pr-fraicheur"><Clock3 size={13} aria-hidden="true" /> Actualisé {fraicheur}</span>
        <button type="button" className="lc-btn lc-btn--ghost" onClick={() => void charger()} disabled={charge}>
          <RefreshCw size={14} aria-hidden="true" /> Actualiser
        </button>
      </div>

      {erreur ? <div className="gd-panel adm-note adm-note--warn"><ShieldAlert size={14} aria-hidden="true" /> {erreur}</div> : null}

      {c ? (
        <div className="pr-kpis">
          <Compteur label="Présents" valeur={c.present} ton="emerald"
            actif={filtreEtat === 'WORKING'} onClick={() => setFiltreEtat(filtreEtat === 'WORKING' ? 'TOUS' : 'WORKING')} />
          <Compteur label="En pause" valeur={c.ON_BREAK} ton="cyan"
            actif={filtreEtat === 'ON_BREAK'} onClick={() => setFiltreEtat(filtreEtat === 'ON_BREAK' ? 'TOUS' : 'ON_BREAK')} />
          <Compteur label="Attendus" valeur={c.UPCOMING_SHIFT} ton="blue"
            actif={filtreEtat === 'UPCOMING_SHIFT'} onClick={() => setFiltreEtat(filtreEtat === 'UPCOMING_SHIFT' ? 'TOUS' : 'UPCOMING_SHIFT')} />
          <Compteur label="Pas encore arrivés" valeur={c.EXPECTED_LATE} ton="amber"
            actif={filtreEtat === 'EXPECTED_LATE'} onClick={() => setFiltreEtat(filtreEtat === 'EXPECTED_LATE' ? 'TOUS' : 'EXPECTED_LATE')} />
          <Compteur label="Hors planning" valeur={c.WORKING_UNPLANNED} ton="violet"
            actif={filtreEtat === 'WORKING_UNPLANNED'} onClick={() => setFiltreEtat(filtreEtat === 'WORKING_UNPLANNED' ? 'TOUS' : 'WORKING_UNPLANNED')} />
          <Compteur label="Terminés" valeur={c.FINISHED} ton="slate"
            actif={filtreEtat === 'FINISHED'} onClick={() => setFiltreEtat(filtreEtat === 'FINISHED' ? 'TOUS' : 'FINISHED')} />
        </div>
      ) : null}

      <div className="pr-filtres">
        <div className="pr-recherche">
          <Search size={14} aria-hidden="true" />
          <TextInput value={q} onChange={setQ} placeholder="Rechercher un salarié" ariaLabel="Rechercher un salarié" />
        </div>
        <Combobox items={equipes} value={equipe} onChange={setEquipe} placeholder="Toutes les équipes" ariaLabel="Filtrer par équipe" />
        <Combobox items={postes} value={poste} onChange={setPoste} placeholder="Tous les postes" ariaLabel="Filtrer par poste" />
        <Combobox items={lieux} value={lieu} onChange={setLieu} placeholder="Tous les lieux" ariaLabel="Filtrer par lieu" />
        <SegmentedControl<'TOUS' | 'SIGNEE' | 'NON'>
          value={signature}
          onChange={setSignature}
          options={[{ value: 'TOUS', label: 'Toutes' }, { value: 'SIGNEE', label: 'Entrée signée' }, { value: 'NON', label: 'Sans signature' }]}
          ariaLabel="Filtrer par signature d’entrée"
        />
      </div>

      {gens.length === 0 ? (
        <div className="gd-panel adm-note">
          {data && data.people.length === 0
            ? 'Personne n’est attendu ni en poste sur cette période.'
            : 'Aucun salarié ne correspond à ces filtres.'}
        </div>
      ) : (
        <ul className="pr-liste">
          {gens.map((p) => (
            <li key={p.person_id} className="pr-carte" {...menu.personne({ id: p.person_id, nom: p.display_name })}>
              <div className="pr-carte__id">
                <span className="pr-nom">{p.display_name}</span>
                <span className="pr-meta">
                  {[p.position_name, p.team_name, p.shift?.location_name].filter(Boolean).join(' · ') || '—'}
                </span>
              </div>
              <div className="pr-carte__etat">
                <Pastille etat={p.state} />
                {p.shift && !p.shift.planned ? <span className="pr-tag pr-tag--violet">Non prévu</span> : null}
                {p.entry_signed
                  ? <span className="pr-tag pr-tag--ok"><PenLine size={11} aria-hidden="true" /> Entrée signée</span>
                  : (p.state === 'WORKING' || p.state === 'WORKING_UNPLANNED' || p.state === 'ON_BREAK')
                    ? <span className="pr-tag pr-tag--warn">Sans signature</span>
                    : null}
              </div>
              <div className="pr-carte__horaires">
                <span><b>Prévu</b> {p.shift ? `${heure(p.shift.starts_at)}–${heure(p.shift.ends_at)}` : '—'}</span>
                <span><b>Entrée</b> {heure(p.clock_in_at)}</span>
                <span><b>Sortie</b> {heure(p.clock_out_at)}</span>
              </div>
              <div className="pr-carte__chiffres">
                {p.current_break
                  ? <span className="pr-pause">En pause depuis {duree(p.current_break.minutes)}</span>
                  : p.summary.break_count > 0
                    ? <span>{p.summary.break_count} pause{p.summary.break_count > 1 ? 's' : ''} · {duree(p.summary.break_minutes)}</span>
                    : <span className="pr-vide">Aucune pause</span>}
                {p.summary.presence_minutes > 0
                  ? <span><b>{duree(p.summary.paid_minutes)}</b> rémunérées</span>
                  : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
